import React, { useState, useEffect } from 'react';
import type { CustomerData } from '../../types';
import type { Staff } from '../../types';

const normalizeCustomerStatus = (status?: string) => {
  const normalized = (status || '').trim();
  if (normalized === '계약완료') return '계약';
  if (normalized === '완료') return '본식완료';
  if (normalized === '취소됨') return '취소';
  return normalized;
};

interface Props {
  isOpen: boolean;
  onClose: () => void;
  data: CustomerData; // 초기 데이터 (부모로부터 받음)
  // setData는 제거 (모달 내부에서 상태 관리)
  onSave: (updatedData: CustomerData) => Promise<void> | void; // 변경된 데이터를 부모에게 전달
}

export default function EditCustomerModal({ isOpen, onClose, data, onSave }: Props) {
  // 1. 모달 내부에서 사용할 폼 상태 정의
  const [formData, setFormData] = useState<CustomerData>({
    ...data,
    status: normalizeCustomerStatus(data.status),
  });
  const [staffList, setStaffList] = useState<Staff[]>([]);

  // ✅ [추가] 모달 열릴 때 직원 목록 조회
  useEffect(() => {
    if (isOpen) {
      setFormData({
        ...data,
        status: normalizeCustomerStatus(data.status),
      }); // 기존 로직

      const fetchStaff = async () => {
        try {
          const token = localStorage.getItem('accessToken');
          // 활성 상태인 직원만, 넉넉하게 100명 조회
          const response = await fetch('https://api.wedmin.biz/user?status=ACTIVE&page=1&size=100', {
            headers: { 'Authorization': `Bearer ${token}` }
          });
          const result = await response.json();
          console.log("result ",result)
          if (result.data.items) { // 또는 result.data (API 응답 구조 확인 필요)
            setStaffList(result.data.items);
          }
        } catch (error) {
          console.error('직원 목록 조회 실패:', error);
        }
      };
      fetchStaff();
    }
  }, [isOpen, data]);

  // 2. 모달이 열릴 때마다 부모의 최신 데이터로 폼 초기화
  useEffect(() => {
    if (isOpen) {
      console.log("data in useEffect ",data)
      setFormData({
        ...data,
        status: normalizeCustomerStatus(data.status),
      });
    }
  }, [isOpen, data]);

  // ✅ [추가] 날짜 문자열을 input type="datetime-local" 형식(YYYY-MM-DDTHH:mm)으로 변환하는 함수
  const formatForInput = (dateStr: string) => {
    if (!dateStr || dateStr === '-' || dateStr === 'string') return '';
    try {
      const date = new Date(dateStr);
      // 유효하지 않은 날짜면 빈 값 반환
      if (isNaN(date.getTime())) return '';
      
      // 한국 시간(KST) 등 로컬 시간대 유지를 위한 오프셋 계산
      const offset = date.getTimezoneOffset() * 60000;
      const localDate = new Date(date.getTime() - offset);
      
      // ISO 문자열에서 앞 16자리(YYYY-MM-DDTHH:mm)만 자름
      return localDate.toISOString().slice(0, 16);
    } catch (e) {
      return '';
    }
  };

  // 3. 입력값 변경 핸들러
  const handleChange = (
    e: React.ChangeEvent<HTMLInputElement | HTMLSelectElement | HTMLTextAreaElement>
  ) => {
    const { name, value } = e.target;
    setFormData((prev) => ({
      ...prev,
      [name]: value,
    }));
  };

  // 4. 저장 버튼 핸들러
  const handleSubmit = async () => {
    // 부모 컴포넌트의 onSave 함수에 변경된 formData를 전달
    const normalizedFormData = {
      ...formData,
      status: normalizeCustomerStatus(formData.status),
    };
    console.log("formData in handleSubmit ", normalizedFormData)
    await onSave(normalizedFormData);
    // (onSave 내부에서 API 호출 성공 시 모달 닫힘 처리를 하거나, 여기서 onClose() 호출)
  };

  if (!isOpen) return null;

  return (
    <div className="fixed inset-0 bg-black/50 flex items-center justify-center z-50 p-4">
      <div className="bg-white rounded-2xl shadow-2xl max-w-5xl w-full max-h-[90vh] overflow-y-auto">
        <div className="sticky top-0 bg-white border-b border-gray-200 px-6 py-4 flex items-center justify-between z-10">
          <h2 className="text-xl font-bold text-gray-900">고객 정보 수정</h2>
          <button 
            onClick={onClose}
            className="text-gray-400 hover:text-gray-600 w-8 h-8 flex items-center justify-center cursor-pointer"
          >
            <i className="ri-close-line text-2xl"></i>
          </button>
        </div>

        <div className="p-6 space-y-6">
          {/* 담당 플래너 정보 */}
          <div>
            <h3 className="text-lg font-semibold text-gray-900 mb-4 flex items-center gap-2">
              <i className="ri-user-star-line text-blue-600"></i>
              담당 플래너 정보
            </h3>

            {/* 담당자 정보 */}

            <div className="grid grid-cols-2 mb-4">
              <div className="col-span-2">
                <label className="block text-sm text-gray-600 mb-2">담당자</label>
                <select
                  name="assignedStaffIdx"
                  value={formData.assignedStaffIdx}
                  onChange={handleChange}
                  className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-blue-500"
                >
                  <option value="">담당자를 선택하세요</option>
                  {staffList.map((staff) => (
                    <option key={staff.userIdx} value={staff.userIdx}>
                      {staff.name}
                    </option>
                  ))}
                </select>
              </div>
            </div>
          </div>

          {/* 고객 기본 정보 */}
          <div>
            <h3 className="text-lg font-semibold text-gray-900 mb-4 flex items-center gap-2">
              <i className="ri-user-heart-line text-pink-600"></i>
              고객 기본 정보
            </h3>
            <div className="grid grid-cols-2 gap-4">
              <div>
                <label className="block text-sm text-gray-600 mb-1">신랑 이름</label>
                <input
                  type="text"
                  name="groomName"
                  value={formData.groomName || ''}
                  onChange={handleChange}
                  className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-blue-500"
                />
              </div>
              <div>
                <label className="block text-sm text-gray-600 mb-1">신부 이름</label>
                <input
                  type="text"
                  name="brideName"
                  value={formData.brideName || ''}
                  onChange={handleChange}
                  className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-blue-500"
                />
              </div>
              <div>
                <label className="block text-sm text-gray-600 mb-1">신랑 연락처</label>
                <input
                  type="text"
                  name="groomContact"
                  value={formData.groomContact}
                  onChange={handleChange}
                  className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-blue-500"
                />
              </div>
              <div>
                <label className="block text-sm text-gray-600 mb-1">신부 연락처</label>
                <input
                  type="text"
                  name="brideContact"
                  value={formData.brideContact}
                  onChange={handleChange}
                  className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-blue-500"
                />
              </div>
            <div className="col-span-2">
              <label className="block text-sm text-gray-600 mb-1">예산</label>
              <input
                type="number"
                name="budget"
                placeholder="예: 20,000,000  (숫자만 입력해주세요.)"
                // 숫자만 입력가능 하도록 제한
                inputMode="numeric"
                pattern="[0-9]*"
                value={formData.budget}
                onChange={handleChange}
                className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-blue-500"
              />
            </div>
            <div className="col-span-2">
              <label className="block text-sm text-gray-600 mb-1">총 계약금액</label>
              <input
                type="number"
                name="totalContractAmount"
                placeholder="예: 60,000,000  (숫자만 입력해주세요.)"
                inputMode="numeric"
                pattern="[0-9]*"
                value={formData.totalContractAmount || ''}
                onChange={handleChange}
                className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-blue-500"
              />
            </div>
 
      
              {/* <div className="col-span-2">
                <label className="block text-sm text-gray-600 mb-1">주소</label>
                <input
                  type="text"
                  name="address"
                  value={formData.address}
                  onChange={handleChange}
                  className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-blue-500"
                />
              </div> */}
            </div>
          </div>

          {/* DB 정보 */}
          <div>
            <h3 className="text-lg font-semibold text-gray-900 mb-4 flex items-center gap-2">
              <i className="ri-database-line text-purple-600"></i>
              DB 정보
            </h3>
            <div className="grid grid-cols-2 gap-4">
              <div>
                <label className="block text-sm text-gray-600 mb-1">유입 경로</label>
                <select
                  name="referralSource"
                  value={formData.referralSource}
                  onChange={handleChange}
                  className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-blue-500 pr-8"
                >
                  <option value="인터넷 광고">인터넷 광고</option>
                  <option value="지인 소개">지인 소개</option>
                  <option value="박람회">박람회</option>
                  <option value="SNS">SNS</option>
                  <option value="기타">기타</option>
                </select>
              </div>
              <div>
                <label className="block text-sm text-gray-600 mb-1">유입 상세</label>
                <input
                  type="text"
                  name="referralSourceDetail"
                  value={formData.referralSourceDetail}
                  onChange={handleChange}
                  className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-blue-500"
                />
              </div>
            </div>
          </div>

          {/* 계약 정보 */}
          <div>
            <h3 className="text-lg font-semibold text-gray-900 mb-4 flex items-center gap-2">
              <i className="ri-file-text-line text-green-600"></i>
              계약 정보
            </h3>
            <div className="grid grid-cols-2 gap-4">
              <div>
                <label className="block text-sm text-gray-600 mb-1">계약 유형</label>
                <select
                  name="contractType"
                  value={formData.contractType}
                  onChange={handleChange}
                  className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-blue-500 pr-8"
                >
                  <option value="리허설+본식">리허설+본식</option>
                  <option value="본식만">본식만</option>
                  <option value="리허설만">리허설만</option>
                </select>
              </div>
              <div>
                <label className="block text-sm text-gray-600 mb-1">계약일</label>
                <input
                  type="date"
                  name="contractDate"
                  value={formData.contractDate}
                  onChange={handleChange}
                  className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-blue-500"
                />
              </div>
              <div>
                <label className="block text-sm text-gray-600 mb-1">계약 업체</label>
                <input
                  type="text"
                  name="contractVendor"
                  value={formData.contractVendor}
                  onChange={handleChange}
                  className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-blue-500"
                />
              </div>
              <div>
                <label className="block text-sm text-gray-600 mb-1">상태</label>
                <select
                  name="status"
                  value={formData.status}
                  onChange={handleChange}
                  className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-blue-500 pr-8"
                >
                  <option value="상담중">상담중</option>
                  <option value="계약">계약</option>
                  <option value="진행중">진행중</option>
                  <option value="본식완료">본식완료</option>
                  <option value="취소">취소</option>
                </select>
              </div>
            </div>
          </div>
          {/* 주요 일정 */}
          <div>
            <h3 className="text-lg font-semibold text-gray-900 mb-4 flex items-center gap-2">
              <i className="ri-calendar-event-line text-purple-600"></i>
              주요 일정
            </h3>
            <div className="grid grid-cols-2 gap-4">
              <div>
                <label className="block text-sm text-gray-600 mb-1">리허설 일시</label>
                <input
                  type="datetime-local"
                  name="rehearsalDateTime"
                  // ✅ 수정: formatForInput 적용
                  value={formatForInput(formData.rehearsalDateTime)}
                  onChange={(e) => {
                    // 저장 시에는 다시 보기 좋은 형태(공백)로 저장하거나 ISO로 저장
                    const val = e.target.value.replace('T', ' '); 
                    setFormData(prev => ({...prev, rehearsalDateTime: val}));
                  }}
                  className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-blue-500"
                />
              </div>
              <div>
                <label className="block text-sm text-gray-600 mb-1">리허설 장소</label>
                <input
                  type="text"
                  name="rehearsalLocation"
                  value={formData.rehearsalLocation}
                  onChange={handleChange}
                  className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-blue-500"
                />
              </div>
              <div>
                <label className="block text-sm text-gray-600 mb-1">본식 일시</label>
                <input
                  type="datetime-local"
                  name="weddingDateTime"
                  // ✅ 수정: formatForInput 적용
                  value={formatForInput(formData.weddingDateTime)}
                  onChange={(e) => {
                    const val = e.target.value.replace('T', ' ');
                    setFormData(prev => ({...prev, weddingDateTime: val}));
                  }}
                  className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-blue-500"
                />
              </div>
              <div>
                <label className="block text-sm text-gray-600 mb-1">본식 장소</label>
                <input
                  type="text"
                  name="weddingLocation"
                  value={formData.weddingLocation}
                  onChange={handleChange}
                  className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-blue-500"
                />
              </div>
            </div>
          </div>

          {/* 특이사항 */}
          <div>
            <h3 className="text-lg font-semibold text-gray-900 mb-4 flex items-center gap-2">
              <i className="ri-sticky-note-line text-yellow-600"></i>
              특이사항
            </h3>
            <textarea
              name="specialNote"
              value={formData.specialNote}
              onChange={handleChange}
              rows={4}
              maxLength={500}
              className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-blue-500 resize-none"
              placeholder="특이사항을 입력하세요 (최대 500자)"
            />
            <div className="text-xs text-gray-500 mt-1 text-right">
              {formData.specialNote ? formData.specialNote.length : 0} / 500
            </div>
          </div>
        </div>

        <div className="sticky bottom-0 bg-gray-50 border-t border-gray-200 px-6 py-4 flex items-center justify-end gap-3 rounded-b-2xl">
          <button 
            onClick={onClose}
            className="px-6 py-2 text-gray-700 bg-white border border-gray-300 rounded-lg hover:bg-gray-50 transition-colors font-medium whitespace-nowrap cursor-pointer"
          >
            취소
          </button>
          <button 
            onClick={handleSubmit}
            className="px-6 py-2 bg-blue-600 text-white rounded-lg hover:bg-blue-700 transition-colors font-medium whitespace-nowrap cursor-pointer"
          >
            저장
          </button>
        </div>
      </div>
    </div>
  );
}
